import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Collapse,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  IconButton,
  Autocomplete,
} from '@mui/material';
import { SystemIcon } from '@monorepo/nova-icons';
import { Filter, FilterUIConfig, FilterOperator } from '../../types';
import { filterUtils } from '../../hooks/data/use-filters';

export interface FilterPanelProps {
  filters: Filter[];
  filterConfigs: FilterUIConfig[];
  onFiltersChange: (filters: Filter[]) => void;
  isOpen?: boolean;
  onToggle?: () => void;
}

export const FilterPanel: React.FC<FilterPanelProps> = ({
  filters,
  filterConfigs,
  onFiltersChange,
  isOpen = false,
  onToggle,
}) => {
  const [newFilter, setNewFilter] = useState<Partial<Filter>>({});

  const addFilter = () => {
    if (
      newFilter.field &&
      newFilter.operator &&
      newFilter.value !== undefined
    ) {
      const filter: Filter = {
        field: newFilter.field,
        operator: newFilter.operator,
        value: newFilter.value,
      };
      onFiltersChange([...filters, filter]);
      setNewFilter({});
    }
  };

  const removeFilter = (index: number) => {
    const newFilters = filters.filter((_, i) => i !== index);
    onFiltersChange(newFilters);
  };

  const clearAllFilters = () => {
    onFiltersChange([]);
  };

  const getFieldConfig = (fieldName: string) => {
    return filterConfigs.find((config) => config.field === fieldName);
  };

  const getAvailableOperators = (fieldName: string) => {
    const config = getFieldConfig(fieldName);
    return (
      config?.operators ||
      filterUtils.getDefaultOperators(config?.type || 'text')
    );
  };

  const renderValueInput = () => {
    if (!newFilter.field) return null;

    const config = getFieldConfig(newFilter.field);
    if (!config) return null;

    switch (config.type) {
      case 'select':
        if (newFilter.operator === 'in' || newFilter.operator === 'notIn') {
          return (
            <Autocomplete
              multiple
              options={config.options || []}
              getOptionLabel={(option) => option.label}
              value={
                config.options?.filter(
                  (opt) =>
                    Array.isArray(newFilter.value) &&
                    newFilter.value.includes(opt.value),
                ) || []
              }
              onChange={(_, value) =>
                setNewFilter((prev) => ({
                  ...prev,
                  value: value.map((v) => v.value),
                }))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Value"
                  placeholder="Select values..."
                  size="small"
                />
              )}
              sx={{ minWidth: 200 }}
            />
          );
        } else {
          return (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Value</InputLabel>
              <Select
                value={newFilter.value || ''}
                label="Value"
                onChange={(e) =>
                  setNewFilter((prev) => ({ ...prev, value: e.target.value }))
                }
              >
                {config.options?.map((option) => (
                  <MenuItem
                    key={String(option.value)}
                    value={option.value as string}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        }

      case 'date':
        return (
          <TextField
            type="date"
            label="Value"
            value={newFilter.value || ''}
            onChange={(e) =>
              setNewFilter((prev) => ({ ...prev, value: e.target.value }))
            }
            size="small"
            sx={{ minWidth: 200 }}
            InputLabelProps={{ shrink: true }}
          />
        );

      case 'number':
        return (
          <TextField
            type="number"
            label="Value"
            value={newFilter.value || ''}
            onChange={(e) =>
              setNewFilter((prev) => ({
                ...prev,
                value: Number(e.target.value),
              }))
            }
            size="small"
            sx={{ minWidth: 200 }}
          />
        );

      default:
        return (
          <TextField
            label="Value"
            value={newFilter.value || ''}
            onChange={(e) =>
              setNewFilter((prev) => ({ ...prev, value: e.target.value }))
            }
            size="small"
            sx={{ minWidth: 200 }}
          />
        );
    }
  };

  return (
    <Box>
      {/* Filter Toggle Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6">Filters</Typography>
          {filters.length > 0 && (
            <Chip label={filters.length} size="small" variant="filled" />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {filters.length > 0 && (
            <Button
              variant="outlined"
              size="sm"
              onClick={clearAllFilters}
              startIcon={<SystemIcon iconName="X" />}
            >
              Clear All
            </Button>
          )}
          {onToggle && (
            <IconButton onClick={onToggle} size="small">
              <SystemIcon
                iconName={isOpen ? 'ArrowUp' : 'ArrowDown'}
                style={{
                  transition: 'transform 0.2s ease',
                }}
              />
            </IconButton>
          )}
        </Box>
      </Box>

      <Collapse in={isOpen}>
        <Box sx={{ p: 2 }}>
          {/* Active Filters */}
          {filters.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Active Filters:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {filters.map((filter, index) => {
                  const config = getFieldConfig(filter.field);
                  return (
                    <Chip
                      key={index}
                      label={`${config?.label || filter.field} ${filterUtils.getOperatorLabel(filter.operator)} ${filterUtils.formatFilterValue(filter, config)}`}
                      onDelete={() => removeFilter(index)}
                      size="small"
                      variant="filled"
                    />
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Add New Filter */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Field</InputLabel>
              <Select
                value={newFilter.field || ''}
                label="Field"
                onChange={(e) => setNewFilter({ field: e.target.value })}
              >
                {filterConfigs.map((config) => (
                  <MenuItem key={config.field} value={config.field}>
                    {config.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {newFilter.field && (
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Operator</InputLabel>
                <Select
                  value={newFilter.operator || ''}
                  label="Operator"
                  onChange={(e) =>
                    setNewFilter((prev) => ({
                      ...prev,
                      operator: e.target.value as FilterOperator,
                      value: undefined, // Reset value when operator changes
                    }))
                  }
                >
                  {getAvailableOperators(newFilter.field).map((operator) => (
                    <MenuItem key={operator} value={operator}>
                      {filterUtils.getOperatorLabel(operator)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {newFilter.field && newFilter.operator && renderValueInput()}

            <Button
              variant="primary"
              onClick={addFilter}
              disabled={
                !newFilter.field ||
                !newFilter.operator ||
                newFilter.value === undefined
              }
              startIcon={<SystemIcon iconName="Plugs" />}
            >
              Add Filter
            </Button>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};
